// The one door from the app into the Rust core.
//
// The offline write queue's rules are `core/src/queue.rs` and the conflict
// classes `core/src/reconcile.rs` (#125), with **no JavaScript twin** —
// there is one implementation of those rules and this is how `src/` reaches it.
// Everything the queue asks (`readyBatches`, `settleBatch`, `deadLetters`,
// `queueDefects`, `ruleViolations`) comes through here.
//
// ── Why this file is in src/domain/ and not src/lib/ (#218) ────────────────
//
// `src/domain/` is copied verbatim into `functions/domain/` by
// `functions/scripts/sync-domain.mjs`, so a domain module importing
// `../lib/core.js` names a file the copy never made —
// `scripts/check-domain-imports.mjs` refuses it by name; otherwise it is
// ERR_MODULE_NOT_FOUND in a deployed function.
//
// Most domain modules that reach the core have intra-domain callers (#214), so
// the door is inside the mirror. `src/lib/core.js` re-exports this for `.jsx`
// files.
//
// ── Why a bare specifier and not a path ────────────────────────────────────
//
// `import('coda-core')` names no file, and the hosts this code runs in resolve
// it to different renderings of the same wasm:
//
//   Vite       `resolve.alias`         → core-ffi/pkg/web/    ESM, async init()
//   Jest       `moduleNameMapper`      → core-ffi/pkg/node/   CommonJS, sync
//   Functions  `file:` dependency      → functions/coda-core/ the same CommonJS
//
// The third (#218) is a real npm dependency rather than a config line: a
// deployed function has neither a Vite config nor a Jest one.
// `functions/coda-core/README.md` has the packaging argument.
//
// **Not `import.meta`.** Branching on `import.meta.env` inside `src/` is the
// #112 parse error: Babel compiles this project's files to CommonJS for Jest,
// where `import.meta` is a syntax error before a single assertion runs (which
// is why `src/lib/firebase.js` is mapped away in the `integration` project). A
// bare specifier moves the branch into config files that Jest, Vite and npm
// each read alone.
//
// Both renderings come from one `cargo build` (see
// `scripts/build-core-wasm.mjs`), so they cannot be of different Rust.
//
// ── Why it is async, and where the one await happens (#214) ────────────────
//
// The web rendering is wasm-bindgen's `--target web`, whose default export is
// an `init()` that fetches the `.wasm`, so the first call cannot be
// synchronous. (The bundler target would be, and needs `vite-plugin-wasm`.)
//
// A render path that asks what a lesson costs has nowhere to ask but the core,
// so the one await happens once per host, before anything asks a question:
// `src/main.jsx` awaits `loadCore()` before the first render,
// `functions/index.js` hands the Node binding in with `setCoreBinding`, and the
// `ui`, `domain`, `functions` and `integration` jest projects prime it in their
// setup. `coreSync()` below is what every caller uses afterwards, and it throws
// rather than returning a plausible zero if it is ever reached first.
//
// ── The promise is cached, not the module ──────────────────────────────────
//
// Two callers racing at startup must not start two `init()`s: the web target
// would fetch and instantiate the wasm twice and hand back two module states.
// Caching the *promise* rather than its result is what makes the second caller
// wait for the first rather than begin again.
//
// A failure is **not** cached: if the wasm fetch fails — a cold load on a
// flaky connection — the next caller tries again rather than inheriting a
// permanent refusal.

let pending = null;
// The resolved binding, or null. Written only by `loadCore`'s success handler,
// read only by `coreSync`.
let binding = null;

async function open() {
  const mod = await import('coda-core');
  // The web rendering's default export is `init()`. The node rendering has no
  // default at all — both Babel's CommonJS interop and Node's own ESM-over-CJS
  // interop then put the whole namespace object there, which is not a function,
  // so this branch tells the web build apart in all three hosts without asking
  // about the environment.
  if (typeof mod.default === 'function') await mod.default();
  return mod;
}

/**
 * The core's binding, once it is instantiated.
 *
 * Idempotent and safe to call from anywhere; every caller after the first gets
 * the same binding without a second instantiation.
 */
export function loadCore() {
  if (!pending) {
    // Two arguments, not `.then(…).catch(…)` (#184): a trailing `.catch` would
    // treat a throw inside the success handler as a failed wasm fetch, clear
    // `pending`, and invite the next caller to fetch again.
    pending = open().then(
      (mod) => {
        binding = mod;
        return mod;
      },
      (err) => {
        pending = null;
        throw err;
      },
    );
  }
  return pending;
}

/**
 * Hand the door an already-instantiated binding, synchronously (#244).
 *
 * For a host that can produce the module without awaiting anything: **the
 * Node rendering of the core instantiates its wasm during module evaluation**
 * — `core-ffi/pkg/node/coda_domain_ffi.js` ends with `readFileSync`,
 * `new WebAssembly.Module`, `new WebAssembly.Instance`, all synchronous — so a
 * plain `import core from 'coda-core'` in `functions/` has the whole binding by
 * the time the import settles.
 *
 * **Awaiting it at module scope breaks every Cloud Function.** A top-level
 * `await loadCore()` in `functions/index.js` is legal ESM, but the functions
 * runtime `require()`s the entry module to invoke a trigger, and Node rejects
 * `require()` of any graph containing a top-level await —
 * `ERR_REQUIRE_ASYNC_MODULE`. The functions load fine and die on every
 * invocation.
 *
 * The browser keeps `loadCore()`. Its rendering is `--target web`, whose
 * `default` export is an `init()` that really does need awaiting, and
 * `src/main.jsx` is not `require()`d by anything.
 *
 * Not a no-op on a second call with a different module: the last caller wins,
 * so there is never a disagreement about which binding is live.
 */
export function setCoreBinding(mod) {
  if (!mod || typeof mod.attendanceId !== 'function') {
    throw new Error(
      'setCoreBinding was handed something that is not the core binding. '
      + 'Expected the module object from `import core from \'coda-core\'` under '
      + 'Node, where the wasm is instantiated synchronously at import time.',
    );
  }
  binding = mod;
  // So a later `loadCore()` resolves to the same object rather than
  // instantiating a second copy of the wasm beside this one.
  pending = Promise.resolve(mod);
  return mod;
}

/**
 * The core's binding, synchronously, for a caller that cannot await.
 *
 * Every render path and every domain rule the core holds. The binding must be
 * loaded first — see the list in the header.
 *
 * **It throws rather than returning null**: a caller that got null would be
 * one `||` away from pricing a lesson at zero. A throw is a blank screen and a
 * stack; a zero is a family invoiced for nothing.
 */
export function coreSync() {
  if (!binding) {
    throw new Error(
      'The Rust core is not loaded yet. Something asked the domain a question '
      + 'before src/main.jsx or functions/index.js awaited loadCore(), or a test '
      + 'reached a domain rule without priming it — see the setup files for the '
      + '`domain`, `ui`, `functions` and `integration` jest projects.',
    );
  }
  return binding;
}

/** Test seam. Forces the next `loadCore()` to instantiate again. */
export function __resetCoreForTests() {
  pending = null;
  binding = null;
}
